import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Shield, Eye, KeyRound, Copy, Users as UsersIcon } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, activeOrg } from "../contexts/AuthContext";

export default function UsersPage() {
  const { user: me } = useAuth();
  const active = activeOrg(me);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", role: "viewer", name: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    if (!active) return;
    setLoading(true);
    api.listOrgMembers(active.id)
      .then(setMembers)
      .catch(() => toast.error("Couldn't load members"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [active?.id]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Email is required"); return; }
    setBusy(true);
    try {
      await api.inviteOrgMember(active.id, form);
      toast.success(`${form.email} added as ${form.role}`);
      setForm({ email: "", password: "", role: "viewer", name: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add member");
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (m.user_id === me.id) { toast.error("You can't remove yourself"); return; }
    if (!window.confirm(`Remove ${m.email} from ${active.name}?`)) return;
    try {
      await api.removeOrgMember(active.id, m.user_id);
      toast.success("Member removed");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.updateOrgMemberRole(active.id, m.user_id, role);
      toast.success(`Role updated to ${role}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const toggleWeeklyWins = async (m) => {
    try {
      await api.setMemberWeeklyWins(active.id, m.user_id, !m.weekly_wins_access);
      toast.success(`Weekly Wins ${m.weekly_wins_access ? "removed from" : "added for"} ${m.email}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const [resetTarget, setResetTarget] = useState(null);   // {user_id, email}
  const [resetPwd, setResetPwd] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetDone, setResetDone] = useState(null);       // {email, password} for copy panel

  const genPwd = () => {
    const w = ["Court", "Zone", "Rise", "Guard", "Ball", "Hoops", "Post", "Wing", "Coach", "Drive"];
    return `${w[Math.floor(Math.random() * w.length)]}${Math.floor(1000 + Math.random() * 9000)}!`;
  };
  const openReset = (m) => {
    setResetTarget({ user_id: m.user_id, email: m.email });
    setResetPwd(genPwd());
    setResetDone(null);
  };
  const closeReset = () => { setResetTarget(null); setResetPwd(""); setResetDone(null); };
  const submitReset = async () => {
    if (resetPwd.length < 6) { toast.error("Password must be 6+ chars"); return; }
    setResetBusy(true);
    try {
      await api.resetMemberPassword(active.id, resetTarget.user_id, resetPwd);
      toast.success("Password reset");
      setResetDone({ email: resetTarget.email, password: resetPwd });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setResetBusy(false); }
  };
  const copyCreds = () => {
    if (!resetDone) return;
    const text = `Maximize Analytics sign-in:\nEmail: ${resetDone.email}\nPassword: ${resetDone.password}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied — send this to the user"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8" data-testid="users-page">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl text-white uppercase tracking-tight leading-none">
            {active?.name || "Organization"} · Members
          </h1>
          <div className="mt-1 h-[2px] w-16" style={{ background: active?.accent_color || "#d8b854" }} />
          <div className="mt-3 text-xs uppercase tracking-widest text-[#a0a0a0]">
            Only org admins can invite or remove members. New emails create fresh accounts.
          </div>
        </div>

        {/* Invite form */}
        <form onSubmit={invite} className="border border-white/10 bg-[#1c1c1c] p-5 space-y-4" data-testid="invite-member-form">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-[var(--org-accent)]" />
            <div className="font-heading text-lg text-white uppercase">Invite Member</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input placeholder="Email" type="email" value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              className="h-10 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
              required data-testid="invite-email" />
            <input placeholder="Name (optional)" value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="h-10 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
              data-testid="invite-name" />
            <input placeholder="Password (only for new account)" type="text" value={form.password}
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
              className="h-10 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)] font-mono"
              minLength={6} data-testid="invite-password" />
            <select value={form.role}
              onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
              className="h-10 px-3 bg-[#101010] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
              data-testid="invite-role">
              <option value="viewer">Viewer (read-only)</option>
              <option value="admin">Admin (full edit)</option>
            </select>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={busy}
              className="flex items-center gap-2 px-5 py-2.5 bg-[var(--org-accent)] text-[#121212] text-xs uppercase tracking-[0.25em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              style={{ transition: "background-color 0.15s ease" }}
              data-testid="invite-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Invite
            </button>
          </div>
        </form>

        {/* Members list */}
        <div className="border border-white/10 bg-[#1c1c1c]" data-testid="members-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#a0a0a0]">
                <th className="px-4 py-3 border-b border-white/10">Email</th>
                <th className="px-4 py-3 border-b border-white/10">Name</th>
                <th className="px-4 py-3 border-b border-white/10">Role</th>
                <th className="px-4 py-3 border-b border-white/10">Add-ons</th>
                <th className="px-4 py-3 border-b border-white/10">Joined</th>
                <th className="px-4 py-3 border-b border-white/10 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[var(--org-accent)]" /></td></tr>
              )}
              {!loading && members.map(m => {
                const restricted = ["family", "trainer", "player", "agent", "lite"].includes(m.role);
                return (
                <tr key={m.user_id} className="hover:bg-white/[0.02]" data-testid={`member-row-${m.user_id}`}>
                  <td className="px-4 py-3 border-b border-white/5 text-white">
                    {restricted ? (
                      <Link
                        to={`/members/${m.user_id}`}
                        className="text-white hover:text-[var(--org-accent)] underline underline-offset-4 decoration-white/20 hover:decoration-[var(--org-accent)]"
                        data-testid={`member-link-${m.user_id}`}
                      >
                        {m.email}
                      </Link>
                    ) : m.email}
                    {m.user_id === me.id && <span className="ml-2 text-[9px] uppercase tracking-widest text-[var(--org-accent)]">(you)</span>}
                    {m.is_super_admin && <span className="ml-2 text-[9px] uppercase tracking-widest text-[var(--org-accent-hover)]">Super-admin</span>}
                  </td>
                  <td className="px-4 py-3 border-b border-white/5 text-[#a0a0a0]">{m.name || "—"}</td>
                  <td className="px-4 py-3 border-b border-white/5">
                    <select
                      value={m.role}
                      disabled={m.user_id === me.id}
                      onChange={(e) => changeRole(m, e.target.value)}
                      className={`h-8 px-2 bg-[#101010] border border-white/10 text-xs font-bold uppercase tracking-widest focus:outline-none focus:border-[var(--org-accent)] ${
                        m.role === "admin" ? "text-[var(--org-accent)]"
                        : m.role === "trainer" ? "text-[#c084fc]"
                        : m.role === "family" ? "text-[#8ec5ff]"
                        : "text-[#60a5fa]"
                      }`}
                      data-testid={`member-role-${m.user_id}`}
                    >
                      <option value="viewer">Viewer (legacy · sees as Agent / Coach)</option>
                      <option value="admin">Admin</option>
                      {/* Family/Trainer only show up in the dropdown for members who already hold that role,
                          so admins can flip family <-> trainer without accidentally promoting a viewer. */}
                      {restricted && <option value="family">Family</option>}
                      {restricted && <option value="player">Player</option>}
                      {restricted && <option value="trainer">Trainer</option>}
                      {restricted && <option value="agent">Agent / Coach</option>}
                      {restricted && <option value="lite">Lite</option>}
                    </select>
                  </td>
                  <td className="px-4 py-3 border-b border-white/5">
                    {m.role === "admin" || m.is_super_admin ? (
                      <span className="text-[10px] uppercase tracking-widest text-[#757575]">All</span>
                    ) : (
                      <button type="button" onClick={() => toggleWeeklyWins(m)}
                        className={`h-7 px-2.5 border text-[10px] uppercase tracking-widest font-bold ${m.weekly_wins_access ? "border-[var(--org-accent)] text-[var(--org-accent)] bg-[var(--org-accent)]/10" : "border-white/10 text-[#757575] hover:text-white hover:border-white/30"}`}
                        style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}
                        title={m.weekly_wins_access ? "Remove Weekly Wins access" : "Add Weekly Wins access (generate / edit / download graphics)"}
                        data-testid={`member-weekly-wins-${m.user_id}`}>
                        {m.weekly_wins_access ? "✓ Weekly Wins" : "+ Weekly Wins"}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b border-white/5 text-[#a0a0a0] font-mono text-xs">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-3 border-b border-white/5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {restricted && (
                        <Link to={`/members/${m.user_id}`}
                          title="Manage player access"
                          className="p-1.5 text-[#757575] hover:text-[var(--org-accent)]"
                          data-testid={`manage-access-${m.user_id}`}>
                          <UsersIcon className="w-4 h-4" />
                        </Link>
                      )}
                      {!m.is_super_admin && (
                        <button onClick={() => openReset(m)}
                          title="Reset password"
                          className="p-1.5 text-[#757575] hover:text-[var(--org-accent)]"
                          style={{ transition: "color 0.15s ease" }}
                          data-testid={`reset-password-${m.user_id}`}>
                          <KeyRound className="w-4 h-4" />
                        </button>
                      )}
                      {m.user_id !== me.id && !m.is_super_admin && (
                        <button onClick={() => remove(m)} className="p-1.5 text-[#757575] hover:text-red-400"
                          style={{ transition: "color 0.15s ease" }}
                          data-testid={`remove-member-${m.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
              {!loading && members.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#757575]">No members yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="reset-password-dialog">
          <div className="bg-[#141414] border border-white/10 w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-[var(--org-accent)]" />
                <div>
                  <h3 className="font-heading text-lg uppercase tracking-[0.15em] text-white">Reset Password</h3>
                  <div className="text-xs text-[#a0a0a0] mt-0.5">{resetTarget.email}</div>
                </div>
              </div>
              <button onClick={closeReset} className="p-2 text-[#a0a0a0] hover:text-white" data-testid="reset-password-close">✕</button>
            </div>
            {!resetDone ? (
              <div className="px-5 py-5 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">New Password</div>
                  <div className="flex gap-2">
                    <input
                      value={resetPwd}
                      onChange={(e) => setResetPwd(e.target.value)}
                      className="flex-1 bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[var(--org-accent)]"
                      data-testid="reset-password-input"
                    />
                    <button onClick={() => setResetPwd(genPwd())}
                      className="px-3 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white"
                      data-testid="reset-password-regen">Regen</button>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button onClick={closeReset}
                    className="px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white">
                    Cancel
                  </button>
                  <button onClick={submitReset} disabled={resetBusy}
                    className="flex items-center gap-2 px-5 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
                    data-testid="reset-password-submit">
                    {resetBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
                    Reset
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-5 py-5" data-testid="reset-password-success">
                <div className="p-3 border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] mb-2">Send These Details</div>
                  <div className="font-mono text-xs text-white space-y-1">
                    <div>Email: {resetDone.email}</div>
                    <div>Password: {resetDone.password}</div>
                  </div>
                  <button onClick={copyCreds}
                    className="mt-3 flex items-center gap-2 px-3 py-1.5 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)]"
                    data-testid="reset-password-copy">
                    <Copy className="w-3 h-3" /> Copy Sign-in Info
                  </button>
                </div>
                <div className="flex items-center justify-end mt-4">
                  <button onClick={closeReset}
                    className="px-5 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]"
                    data-testid="reset-password-done">
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
